import { Badge } from "@package/subframe/components/Badge";
import { Button } from "@package/subframe/components/Button";
import { IconButton } from "@package/subframe/components/IconButton";
import { Tabs } from "@package/subframe/components/Tabs";
import { FeatherArrowLeft } from "@subframe/core";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { format } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { EditTab } from "@/components/schedule/EditTab";
import { PauseTemplateDialog } from "@/components/schedule/PauseTemplateDialog";
import { PublishSuccessDialog } from "@/components/schedule/PublishSuccessDialog";
import { PublishTemplateDialog } from "@/components/schedule/PublishTemplateDialog";
import { QuestionsTab } from "@/components/schedule/QuestionsTab";
import { RegenerateInstancesDialog } from "@/components/schedule/RegenerateInstancesDialog";
import {
  AssigneeType,
  DAYS,
  ROLES,
  ScheduleTab,
} from "@/components/schedule/ScheduleTab";
import { Spinner } from "@/components/ui/spinner";
import { useAuth } from "@/contexts/AuthContext";
import { useNursery } from "@/contexts/NurseryContext";
import { useListCategoriesQueryControllerList } from "@/generated/categories/categories";
import { useListNurseryMembersQueryControllerListMembers } from "@/generated/members/members";
import {
  AddQuestionnaireConfigDtoAssigneeRolesItem,
  AddQuestionnaireConfigDtoCompletionMode,
  AddQuestionnaireConfigDtoFrequency,
  AddQuestionnaireConfigDtoMissBehaviour,
  TemplateResponseDtoStatus,
  UpdateQuestionnaireConfigDtoAssigneeRolesItem,
  UpdateQuestionnaireConfigDtoCompletionMode,
  UpdateQuestionnaireConfigDtoFrequency,
  UpdateQuestionnaireConfigDtoMissBehaviour,
  UpdateQuestionnaireConfigDtoStatus,
} from "@/generated/model";
import {
  useAddQuestionnaireConfigCommandControllerAddConfig,
  useUpdateQuestionnaireConfigCommandControllerUpdateConfig,
} from "@/generated/questionnaire-configs/questionnaire-configs";
import { useGetTemplateByIdQueryControllerGetTemplateById } from "@/generated/questionnaire-templates/questionnaire-templates";

/** Convert a local Date to a UTC midnight ISO string, preserving the calendar date */
function toUTCDateString(date: Date): string {
  return new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
  ).toISOString();
}

export const Route = createFileRoute(
  "/_authenticated/templates/$templateId/schedule",
)({
  component: ScheduleTemplatePage,
});

// Parse RRule to extract schedule data
function parseScheduleRule(scheduleRule: string | null | undefined) {
  if (!scheduleRule)
    return {
      frequency: AddQuestionnaireConfigDtoFrequency.WEEKLY,
      days: ["MO"],
      startDate: undefined,
    };

  const lines = scheduleRule.split("\n");
  let frequency: AddQuestionnaireConfigDtoFrequency =
    AddQuestionnaireConfigDtoFrequency.WEEKLY;
  let days: string[] = ["MO"];
  let startDate: Date | undefined;

  for (const line of lines) {
    if (line.startsWith("DTSTART:")) {
      const dateStr = line.replace("DTSTART:", "");
      const year = parseInt(dateStr.substring(0, 4));
      const month = parseInt(dateStr.substring(4, 6)) - 1;
      const day = parseInt(dateStr.substring(6, 8));
      startDate = new Date(year, month, day);
    }
    if (line.includes("RRULE:")) {
      const rrule = line.replace("RRULE:", "");
      const parts = rrule.split(";");
      for (const part of parts) {
        if (part.startsWith("FREQ=")) {
          const freq = part.replace("FREQ=", "");
          if (freq === "DAILY")
            frequency = AddQuestionnaireConfigDtoFrequency.DAILY;
          else if (freq === "WEEKLY")
            frequency = AddQuestionnaireConfigDtoFrequency.WEEKLY;
          else if (freq === "MONTHLY")
            frequency = AddQuestionnaireConfigDtoFrequency.MONTHLY;
        }
        if (part.startsWith("BYDAY=")) {
          days = part.replace("BYDAY=", "").split(",");
        }
      }
    }
  }

  return { frequency, days, startDate };
}

function ScheduleTemplatePage() {
  const { templateId } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { selectedNursery, selectedNurseryId } = useNursery();
  const { userRoleType } = useAuth();

  const isAdmin =
    userRoleType === "PLATFORM_ADMIN" || userRoleType === "ORG_ADMIN";

  // Tab state
  const [activeTab, setActiveTab] = useState<"preview" | "edit" | "schedule">(
    "preview",
  );

  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);

  // Form state
  const [frequency, setFrequency] =
    useState<AddQuestionnaireConfigDtoFrequency>(
      AddQuestionnaireConfigDtoFrequency.WEEKLY,
    );
  const [selectedDays, setSelectedDays] = useState<string[]>(["MO"]);
  const [availableFromDayOfWeek, setAvailableFromDayOfWeek] =
    useState<string>("MO");
  const [dueByDayOfWeek, setDueByDayOfWeek] = useState<string>("FR");
  const [missBehaviour, setMissBehaviour] =
    useState<AddQuestionnaireConfigDtoMissBehaviour>(
      AddQuestionnaireConfigDtoMissBehaviour.LATE_ALLOWED,
    );
  const [availableFromDayOfMonth, setAvailableFromDayOfMonth] =
    useState<number>(1);
  const [dueByDayOfMonth, setDueByDayOfMonth] = useState<number>(28);
  const [dueByType, setDueByType] = useState<"end_of_day" | "custom">(
    "end_of_day",
  );
  const [dueByTime, setDueByTime] = useState("18:00");
  const [availableFromTime, setAvailableFromTime] = useState("09:00");
  const [startDate, setStartDate] = useState<Date | undefined>(new Date());
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [assigneeType, setAssigneeType] = useState<AssigneeType>("role");
  const [assigneeRoles, setAssigneeRoles] = useState<
    AddQuestionnaireConfigDtoAssigneeRolesItem[]
  >([AddQuestionnaireConfigDtoAssigneeRolesItem.PRACTITIONER]);
  const [assigneeUserIds, setAssigneeUserIds] = useState<string[]>([]);
  const [completionMode, setCompletionMode] =
    useState<AddQuestionnaireConfigDtoCompletionMode>(
      AddQuestionnaireConfigDtoCompletionMode.ANY,
    );
  const [primaryCategoryId, setPrimaryCategoryId] = useState<string>("");
  const [secondaryCategoryIds, setSecondaryCategoryIds] = useState<string[]>(
    [],
  );

  // Dialog states
  const [isPublishDialogOpen, setIsPublishDialogOpen] = useState(false);
  const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState(false);
  const [isPauseDialogOpen, setIsPauseDialogOpen] = useState(false);
  const [isRegenerateDialogOpen, setIsRegenerateDialogOpen] = useState(false);

  // Fetch template details
  const {
    data: templateData,
    isLoading: templateLoading,
    refetch: refetchTemplate,
  } = useGetTemplateByIdQueryControllerGetTemplateById(templateId, {
    query: { enabled: !!templateId },
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const template = (templateData as any)?.data;

  // Fetch nursery members for team member assignment
  const { data: membersData, isLoading: membersLoading } =
    useListNurseryMembersQueryControllerListMembers(
      { nurseryId: selectedNurseryId || undefined },
      { query: { enabled: !!selectedNurseryId } },
    );

  const nurseryMembers = useMemo(
    () => membersData?.data?.data || [],
    [membersData?.data?.data],
  );

  // Active category catalog for the multi-select. Names are resolved from here;
  // the config only stores categoryIds. The list endpoint returns active
  // categories, already ordered by sortOrder.
  const { data: categoriesResponse } = useListCategoriesQueryControllerList();
  const categories = useMemo(
    () => categoriesResponse?.data.items ?? [],
    [categoriesResponse?.data.items],
  );

  // Find existing config for this template and nursery
  const existingConfig = useMemo(() => {
    const configs = template?.nurseryConfigs || [];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return configs.find((c: any) => c.nurseryId === selectedNurseryId);
  }, [template?.nurseryConfigs, selectedNurseryId]);

  // Populate form from existing config
  useEffect(() => {
    if (existingConfig) {
      // Determine frequency
      const freq = existingConfig.frequency
        ? (existingConfig.frequency as AddQuestionnaireConfigDtoFrequency)
        : parseScheduleRule(existingConfig.scheduleRule).frequency;
      setFrequency(freq);

      // Parse schedule rule for days/startDate (needed for weekly and legacy configs)
      const parsed = parseScheduleRule(existingConfig.scheduleRule);
      setSelectedDays(parsed.days);
      if (parsed.startDate) setStartDate(parsed.startDate);

      if (existingConfig.startTime) {
        setAvailableFromTime(existingConfig.startTime);
      }

      if (existingConfig.endTime) {
        setDueByTime(existingConfig.endTime);
        if (existingConfig.endTime === "23:59") {
          setDueByType("end_of_day");
        } else {
          setDueByType("custom");
        }
      }

      // Populate new fields
      if (existingConfig.missBehaviour) {
        setMissBehaviour(
          existingConfig.missBehaviour as AddQuestionnaireConfigDtoMissBehaviour,
        );
      }
      if (existingConfig.activeStartDate) {
        setStartDate(new Date(existingConfig.activeStartDate));
      }
      if (existingConfig.activeEndDate) {
        setEndDate(new Date(existingConfig.activeEndDate));
      }
      if (existingConfig.availableFromDayOfMonth) {
        setAvailableFromDayOfMonth(existingConfig.availableFromDayOfMonth);
      }
      if (existingConfig.dueByDayOfMonth) {
        setDueByDayOfMonth(existingConfig.dueByDayOfMonth);
      }
      if (existingConfig.availableFromDayOfWeek) {
        setAvailableFromDayOfWeek(existingConfig.availableFromDayOfWeek);
      }
      if (existingConfig.dueByDayOfWeek) {
        setDueByDayOfWeek(existingConfig.dueByDayOfWeek);
      }

      if (existingConfig.completionMode) {
        setCompletionMode(
          existingConfig.completionMode as AddQuestionnaireConfigDtoCompletionMode,
        );
      }

      const primary: string = existingConfig.primaryCategoryId ?? "";
      setPrimaryCategoryId(primary);
      setSecondaryCategoryIds(
        ((existingConfig.categoryIds as string[] | undefined) ?? []).filter(
          (id) => id !== primary,
        ),
      );

      // Determine assignee type and populate accordingly
      if (existingConfig.assigneeUserIds?.length > 0) {
        setAssigneeType("team_members");
        setAssigneeUserIds(existingConfig.assigneeUserIds);
        setAssigneeRoles([]);
      } else if (existingConfig.assigneeRoles?.length > 0) {
        setAssigneeType("role");
        setAssigneeRoles(
          existingConfig.assigneeRoles as AddQuestionnaireConfigDtoAssigneeRolesItem[],
        );
        setAssigneeUserIds([]);
      }
    }
  }, [existingConfig]);

  // Invalidate caches helper
  const invalidateCaches = async () => {
    await Promise.all([
      refetchTemplate(),
      queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          query.queryKey[0] === "/v1/questionnaire-templates",
        refetchType: "all",
      }),
      queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          typeof query.queryKey[0] === "string" &&
          query.queryKey[0].includes("/scheduled-questionnaires"),
        refetchType: "all",
      }),
    ]);
  };

  // Mutations
  const addConfigMutation = useAddQuestionnaireConfigCommandControllerAddConfig(
    {
      mutation: { onSuccess: invalidateCaches },
    },
  );

  const updateConfigMutation =
    useUpdateQuestionnaireConfigCommandControllerUpdateConfig({
      mutation: { onSuccess: invalidateCaches },
    });

  // Build schedule rule in RRule format
  const buildScheduleRule = (): string => {
    if (frequency === AddQuestionnaireConfigDtoFrequency.ONE_TIME) return "";
    if (frequency === AddQuestionnaireConfigDtoFrequency.MONTHLY) {
      if (!startDate) return "";
      const dtstart = format(startDate, "yyyyMMdd'T'000000");
      return `DTSTART:${dtstart}\nRRULE:FREQ=MONTHLY;BYMONTHDAY=${availableFromDayOfMonth}`;
    }
    if (!startDate) return "";
    const dtstart = format(startDate, "yyyyMMdd'T'000000");
    if (frequency === AddQuestionnaireConfigDtoFrequency.DAILY) {
      return `DTSTART:${dtstart}\nRRULE:FREQ=DAILY;BYDAY=MO,TU,WE,TH,FR`;
    }
    // WEEKLY - use the available-from day as the single BYDAY
    return `DTSTART:${dtstart}\nRRULE:FREQ=WEEKLY;BYDAY=${availableFromDayOfWeek}`;
  };

  const endTime =
    frequency === AddQuestionnaireConfigDtoFrequency.ONE_TIME
      ? dueByTime
      : dueByType === "end_of_day"
        ? "23:59"
        : dueByTime;

  // Generate summary text
  const summary = useMemo(() => {
    let assigneeText = "None selected";
    if (assigneeType === "team_members") {
      if (assigneeUserIds.length > 0) {
        const names = assigneeUserIds
          .map((id) => {
            const member = nurseryMembers.find((m) => m.userId === id);
            if (!member) return null;
            return (
              [member.firstName, member.lastName].filter(Boolean).join(" ") ||
              member.email
            );
          })
          .filter(Boolean);
        assigneeText = names.join(", ");
      }
    } else {
      if (assigneeRoles.length > 0) {
        assigneeText = assigneeRoles
          .map((r) => ROLES.find((role) => role.value === r)?.label)
          .join(", ");
      }
    }

    const dueText = dueByType === "end_of_day" ? "end of day" : dueByTime;
    const missText =
      missBehaviour === AddQuestionnaireConfigDtoMissBehaviour.HARD_MISS
        ? "Hard miss (no late completion)"
        : "Late completion allowed";
    const lines: string[] = [];

    if (frequency === AddQuestionnaireConfigDtoFrequency.ONE_TIME) {
      const startText = startDate
        ? format(startDate, "d MMMM yyyy")
        : "not set";
      const endText = endDate ? format(endDate, "d MMMM yyyy") : "not set";
      lines.push(
        `One-time, available from ${startText} at ${availableFromTime}, due by ${endText} at ${dueByTime}`,
      );
    } else if (frequency === AddQuestionnaireConfigDtoFrequency.DAILY) {
      lines.push(
        `Runs daily, available from ${availableFromTime} and due by ${dueText}`,
      );
    } else if (frequency === AddQuestionnaireConfigDtoFrequency.MONTHLY) {
      lines.push(
        `Runs monthly, available from day ${availableFromDayOfMonth} and due by day ${dueByDayOfMonth}`,
      );
    } else {
      const fromDayLabel =
        DAYS.find((d) => d.key === availableFromDayOfWeek)?.label ||
        availableFromDayOfWeek;
      const toDayLabel =
        DAYS.find((d) => d.key === dueByDayOfWeek)?.label || dueByDayOfWeek;
      lines.push(
        `Runs weekly, available from ${fromDayLabel} ${availableFromTime} and due by ${toDayLabel} ${dueText}`,
      );
    }

    lines.push(
      `Created for ${assigneeText} across ${selectedNursery?.name || "selected nursery"}`,
    );
    if (completionMode === AddQuestionnaireConfigDtoCompletionMode.INDIVIDUAL) {
      lines.push("Each assigned member must complete individually");
    }
    lines.push(missText);

    if (
      frequency !== AddQuestionnaireConfigDtoFrequency.ONE_TIME &&
      startDate &&
      endDate
    ) {
      lines.push(
        `Runs from ${format(startDate, "d MMMM yyyy")} and ends on ${format(endDate, "d MMMM yyyy")}`,
      );
    }

    return lines;
  }, [
    frequency,
    dueByType,
    dueByTime,
    availableFromTime,
    assigneeType,
    assigneeRoles,
    assigneeUserIds,
    nurseryMembers,
    selectedNursery,
    startDate,
    endDate,
    missBehaviour,
    availableFromDayOfMonth,
    dueByDayOfMonth,
    availableFromDayOfWeek,
    dueByDayOfWeek,
    completionMode,
  ]);

  // Handlers
  const handleSave = async () => {
    if (!selectedNurseryId) {
      toast.error("Please select a nursery first");
      return;
    }
    if (
      frequency === AddQuestionnaireConfigDtoFrequency.ONE_TIME &&
      (!startDate || !endDate)
    ) {
      toast.error("Please select both a start date and due date");
      return;
    }
    if (assigneeType === "role" && assigneeRoles.length === 0) {
      toast.error("Please select at least one role");
      return;
    }
    if (assigneeType === "team_members" && assigneeUserIds.length === 0) {
      toast.error("Please select at least one team member");
      return;
    }

    try {
      await addConfigMutation.mutateAsync({
        nurseryId: selectedNurseryId,
        data: {
          questionnaireTemplateId: templateId,
          scheduleRule: buildScheduleRule() || undefined,
          startTime: availableFromTime,
          endTime,
          assigneeRoles: assigneeType === "role" ? assigneeRoles : undefined,
          assigneeUserIds:
            assigneeType === "team_members" ? assigneeUserIds : undefined,
          frequency,
          missBehaviour,
          activeStartDate: startDate ? toUTCDateString(startDate) : undefined,
          activeEndDate: endDate ? toUTCDateString(endDate) : undefined,
          availableFromDayOfMonth:
            frequency === AddQuestionnaireConfigDtoFrequency.MONTHLY
              ? availableFromDayOfMonth
              : undefined,
          dueByDayOfMonth:
            frequency === AddQuestionnaireConfigDtoFrequency.MONTHLY
              ? dueByDayOfMonth
              : undefined,
          availableFromDayOfWeek:
            frequency === AddQuestionnaireConfigDtoFrequency.WEEKLY
              ? availableFromDayOfWeek
              : undefined,
          dueByDayOfWeek:
            frequency === AddQuestionnaireConfigDtoFrequency.WEEKLY
              ? dueByDayOfWeek
              : undefined,
          completionMode,
          // Primary + secondary are both optional; send whatever's selected.
          categoryIds: [
            ...(primaryCategoryId ? [primaryCategoryId] : []),
            ...secondaryCategoryIds,
          ],
          primaryCategoryId: primaryCategoryId || undefined,
        },
      });
      toast.success("Schedule saved successfully");
    } catch {
      toast.error("Failed to save schedule");
    }
  };

  const handleUpdate = async () => {
    if (!selectedNurseryId || !existingConfig?.id) {
      toast.error("No config to update");
      return;
    }
    if (
      frequency === AddQuestionnaireConfigDtoFrequency.ONE_TIME &&
      (!startDate || !endDate)
    ) {
      toast.error("Please select both a start date and due date");
      return;
    }
    if (assigneeType === "role" && assigneeRoles.length === 0) {
      toast.error("Please select at least one role");
      return;
    }
    if (assigneeType === "team_members" && assigneeUserIds.length === 0) {
      toast.error("Please select at least one team member");
      return;
    }

    try {
      await updateConfigMutation.mutateAsync({
        nurseryId: selectedNurseryId,
        configId: existingConfig.id,
        data: {
          scheduleRule: buildScheduleRule() || undefined,
          startTime: availableFromTime,
          endTime,
          assigneeRoles:
            assigneeType === "role"
              ? (assigneeRoles as unknown as UpdateQuestionnaireConfigDtoAssigneeRolesItem[])
              : [],
          assigneeUserIds:
            assigneeType === "team_members" ? assigneeUserIds : [],
          frequency:
            frequency as string as UpdateQuestionnaireConfigDtoFrequency,
          missBehaviour:
            missBehaviour as string as UpdateQuestionnaireConfigDtoMissBehaviour,
          activeStartDate: startDate ? toUTCDateString(startDate) : undefined,
          activeEndDate: endDate ? toUTCDateString(endDate) : null,
          availableFromDayOfMonth:
            frequency === AddQuestionnaireConfigDtoFrequency.MONTHLY
              ? availableFromDayOfMonth
              : undefined,
          dueByDayOfMonth:
            frequency === AddQuestionnaireConfigDtoFrequency.MONTHLY
              ? dueByDayOfMonth
              : undefined,
          availableFromDayOfWeek:
            frequency === AddQuestionnaireConfigDtoFrequency.WEEKLY
              ? availableFromDayOfWeek
              : undefined,
          dueByDayOfWeek:
            frequency === AddQuestionnaireConfigDtoFrequency.WEEKLY
              ? dueByDayOfWeek
              : undefined,
          completionMode:
            completionMode as string as UpdateQuestionnaireConfigDtoCompletionMode,
          // Primary + secondary are both optional; send whatever's selected.
          categoryIds: [
            ...(primaryCategoryId ? [primaryCategoryId] : []),
            ...secondaryCategoryIds,
          ],
          // "" explicitly clears the primary on the API (undefined would mean
          // "leave unchanged" for a partial update).
          primaryCategoryId,
        },
      });
      setIsEditing(false);
      if (isPublished) {
        setIsRegenerateDialogOpen(true);
      } else {
        toast.success("Schedule updated successfully");
      }
    } catch {
      toast.error("Failed to update schedule");
    }
  };

  const handlePublish = async () => {
    if (!selectedNurseryId || !existingConfig?.id) {
      toast.error("Please save the schedule first");
      return;
    }

    try {
      await updateConfigMutation.mutateAsync({
        nurseryId: selectedNurseryId,
        configId: existingConfig.id,
        data: { status: UpdateQuestionnaireConfigDtoStatus.PUBLISHED },
      });
      setIsPublishDialogOpen(false);
      setIsSuccessDialogOpen(true);
    } catch (error: unknown) {
      setIsPublishDialogOpen(false);
      toast.error(
        error instanceof Error ? error.message : "Failed to publish template",
      );
    }
  };

  const handlePause = async () => {
    if (!selectedNurseryId || !existingConfig?.id) {
      toast.error("No config to pause");
      return;
    }

    try {
      await updateConfigMutation.mutateAsync({
        nurseryId: selectedNurseryId,
        configId: existingConfig.id,
        data: { status: UpdateQuestionnaireConfigDtoStatus.PAUSED },
      });
      setIsPauseDialogOpen(false);
      toast.success("Template paused successfully");
    } catch (error: unknown) {
      setIsPauseDialogOpen(false);
      toast.error(
        error instanceof Error ? error.message : "Failed to pause template",
      );
    }
  };

  const handleRegenerateInstances = async () => {
    if (!selectedNurseryId || !existingConfig?.id) return;

    try {
      // Pause to cancel future instances
      await updateConfigMutation.mutateAsync({
        nurseryId: selectedNurseryId,
        configId: existingConfig.id,
        data: { status: UpdateQuestionnaireConfigDtoStatus.PAUSED },
      });
      // Republish to regenerate with new schedule
      await updateConfigMutation.mutateAsync({
        nurseryId: selectedNurseryId,
        configId: existingConfig.id,
        data: { status: UpdateQuestionnaireConfigDtoStatus.PUBLISHED },
      });
      setIsRegenerateDialogOpen(false);
      toast.success("Future instances updated with new schedule");
    } catch {
      setIsRegenerateDialogOpen(false);
      toast.error("Failed to apply changes to future instances");
    }
  };

  // Derived state
  const isLoading = templateLoading;
  const hasExistingConfig = !!existingConfig;
  const isPublished = existingConfig?.status === "PUBLISHED";

  const assigneeDisplay = useMemo(() => {
    if (assigneeType === "team_members") {
      if (assigneeUserIds.length === 0) return "Not assigned";
      const names = assigneeUserIds
        .map((id) => {
          const member = nurseryMembers.find((m) => m.userId === id);
          if (!member) return null;
          return (
            [member.firstName, member.lastName].filter(Boolean).join(" ") ||
            member.email
          );
        })
        .filter(Boolean);
      return names.join(", ");
    }
    if (assigneeRoles.length === 0) return "Not assigned";
    return assigneeRoles
      .map((r) => ROLES.find((role) => role.value === r)?.label)
      .join(", ");
  }, [assigneeType, assigneeRoles, assigneeUserIds, nurseryMembers]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner className="h-8 w-8 text-primary" />
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100%+4rem)] w-[calc(100%+4rem)] -m-8 mobile:w-[calc(100%+2rem)] mobile:h-[calc(100%+2rem)] mobile:-m-4 flex-col items-center bg-alternate-background animate-fade-in overflow-y-auto">
      {/* Header Section */}
      <div className="flex w-full flex-col items-center gap-2 px-8 py-8">
        <div className="flex w-full max-w-[1024px] flex-col items-start gap-6">
          <div className="flex w-full flex-col items-start gap-6">
            <div className="flex w-full flex-wrap items-center gap-6">
              <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
                <div className="flex w-full items-start gap-2">
                  <IconButton
                    icon={<FeatherArrowLeft />}
                    onClick={() => navigate({ to: "/templates" })}
                  />
                  <div className="flex grow shrink-0 basis-0 flex-col items-start gap-2">
                    <span className="w-full whitespace-nowrap text-metrics font-metrics text-primary-navy-800">
                      {template?.name || "Template"}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-end gap-2">
                {isPublished ? (
                  <Button
                    variant="neutral-secondary"
                    size="large"
                    onClick={() => setIsPauseDialogOpen(true)}
                  >
                    Pause this template
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="neutral-primary"
                      size="large"
                      onClick={() => navigate({ to: "/templates" })}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="neutral-primary"
                      size="large"
                      onClick={hasExistingConfig ? handleUpdate : handleSave}
                      disabled={
                        addConfigMutation.isPending ||
                        updateConfigMutation.isPending
                      }
                    >
                      {addConfigMutation.isPending ||
                      updateConfigMutation.isPending
                        ? "Saving..."
                        : "Save"}
                    </Button>
                    <Button
                      size="large"
                      onClick={() => setIsPublishDialogOpen(true)}
                      disabled={!hasExistingConfig}
                    >
                      Publish to staff
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Info Section */}
          <div className="flex w-full flex-col items-start gap-4">
            <div className="flex w-full flex-col items-start gap-4">
              <div className="flex w-full max-w-[576px] flex-wrap items-start gap-4">
                <div className="flex min-w-[128px] grow shrink-0 basis-0 flex-col items-start gap-2">
                  <span className="text-caption font-caption text-subtext-color">
                    Status
                  </span>
                  <Badge
                    variant={
                      isPublished
                        ? "success"
                        : hasExistingConfig
                          ? "warning"
                          : "neutral"
                    }
                  >
                    {isPublished
                      ? "Published"
                      : hasExistingConfig
                        ? "Draft"
                        : "Not in use"}
                  </Badge>
                </div>
                <div className="flex min-w-[128px] grow shrink-0 basis-0 flex-col items-start gap-2">
                  <span className="text-caption font-caption text-subtext-color">
                    Questions
                  </span>
                  <span className="text-body-bold font-body-bold text-default-font">
                    {template?.questions?.length || 0}
                  </span>
                </div>
                <div className="flex min-w-[128px] grow shrink-0 basis-0 flex-col items-start gap-2">
                  <span className="text-caption font-caption text-subtext-color">
                    Assigned to
                  </span>
                  <div className="flex items-start gap-2">
                    {hasExistingConfig ? (
                      <Badge variant="neutral">{assigneeDisplay}</Badge>
                    ) : (
                      <span className="text-body font-body text-subtext-color">
                        Not assigned
                      </span>
                    )}
                  </div>
                </div>
              </div>
              {template?.description && (
                <span className="text-body font-body text-subtext-color">
                  {template.description}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex w-full max-w-[1024px] flex-col items-start justify-end px-4">
        <Tabs>
          <Tabs.Item
            active={activeTab === "preview"}
            onClick={() => setActiveTab("preview")}
          >
            Preview
          </Tabs.Item>
          {isAdmin && (
            <Tabs.Item
              active={activeTab === "edit"}
              onClick={() => setActiveTab("edit")}
            >
              Edit
            </Tabs.Item>
          )}
          {isAdmin && (
            <Tabs.Item
              active={activeTab === "schedule"}
              onClick={() => setActiveTab("schedule")}
            >
              Schedule
            </Tabs.Item>
          )}
        </Tabs>
      </div>

      {/* Tab Content */}
      {!isAdmin || activeTab === "preview" ? (
        <QuestionsTab questions={template?.questions || []} />
      ) : activeTab === "edit" && isAdmin ? (
        <EditTab
          templateId={templateId}
          questions={template?.questions || []}
          isTemplatePublished={
            template?.status === TemplateResponseDtoStatus.PUBLISHED
          }
          onQuestionsChanged={() => refetchTemplate()}
          onTemplateVersionCreated={(newTemplateId) => {
            navigate({
              to: "/templates/$templateId/schedule",
              params: { templateId: newTemplateId },
            });
          }}
        />
      ) : (
        <div className="flex w-full items-center justify-center px-6 pt-8 pb-6">
          <div className="flex w-full max-w-[1024px] flex-col items-start">
            <ScheduleTab
              hasExistingConfig={hasExistingConfig}
              isEditing={isEditing}
              isPublished={isPublished}
              frequency={frequency}
              selectedDays={selectedDays}
              dueByType={dueByType}
              dueByTime={dueByTime}
              availableFromTime={availableFromTime}
              startDate={startDate}
              endDate={endDate}
              assigneeType={assigneeType}
              assigneeRoles={assigneeRoles}
              assigneeUserIds={assigneeUserIds}
              nurseryMembers={nurseryMembers}
              categories={categories}
              primaryCategoryId={primaryCategoryId}
              secondaryCategoryIds={secondaryCategoryIds}
              summary={summary}
              nurseryName={selectedNursery?.name}
              missBehaviour={missBehaviour}
              availableFromDayOfMonth={availableFromDayOfMonth}
              dueByDayOfMonth={dueByDayOfMonth}
              availableFromDayOfWeek={availableFromDayOfWeek}
              dueByDayOfWeek={dueByDayOfWeek}
              completionMode={completionMode}
              isSaving={addConfigMutation.isPending}
              isUpdating={updateConfigMutation.isPending}
              isMembersLoading={membersLoading}
              setIsEditing={setIsEditing}
              setFrequency={setFrequency}
              setSelectedDays={setSelectedDays}
              setDueByType={setDueByType}
              setDueByTime={setDueByTime}
              setAvailableFromTime={setAvailableFromTime}
              setStartDate={setStartDate}
              setEndDate={setEndDate}
              setAssigneeType={(type) => {
                setAssigneeType(type);
                // Picking specific users implies each owns their own instance;
                // without this, the form's default `ANY` would persist and the
                // backend (pre-NAU-238) created a shared instance with
                // `assignedToUserId = null`, locking the named user out.
                if (type === "team_members") {
                  setCompletionMode(
                    AddQuestionnaireConfigDtoCompletionMode.INDIVIDUAL,
                  );
                }
              }}
              setAssigneeRoles={setAssigneeRoles}
              setAssigneeUserIds={setAssigneeUserIds}
              setPrimaryCategoryId={setPrimaryCategoryId}
              setSecondaryCategoryIds={setSecondaryCategoryIds}
              setMissBehaviour={setMissBehaviour}
              setAvailableFromDayOfMonth={setAvailableFromDayOfMonth}
              setDueByDayOfMonth={setDueByDayOfMonth}
              setAvailableFromDayOfWeek={setAvailableFromDayOfWeek}
              setDueByDayOfWeek={setDueByDayOfWeek}
              setCompletionMode={setCompletionMode}
              onSave={handleSave}
              onUpdate={handleUpdate}
            />
          </div>
        </div>
      )}

      {/* Dialogs */}
      <PublishTemplateDialog
        open={isPublishDialogOpen}
        onOpenChange={setIsPublishDialogOpen}
        templateName={template?.name || ""}
        questionCount={template?.questions?.length || 0}
        frequency={frequency}
        selectedDays={selectedDays}
        dueByType={dueByType}
        dueByTime={dueByTime}
        availableFromTime={availableFromTime}
        startDate={startDate}
        endDate={endDate}
        assigneeType={assigneeType}
        assigneeRoles={assigneeRoles}
        assigneeDisplay={assigneeDisplay}
        nurseryName={selectedNursery?.name}
        missBehaviour={missBehaviour}
        availableFromDayOfMonth={availableFromDayOfMonth}
        dueByDayOfMonth={dueByDayOfMonth}
        availableFromDayOfWeek={availableFromDayOfWeek}
        dueByDayOfWeek={dueByDayOfWeek}
        completionMode={completionMode}
        isPublishing={updateConfigMutation.isPending}
        onPublish={handlePublish}
      />

      <PublishSuccessDialog
        open={isSuccessDialogOpen}
        templateName={template?.name || ""}
        onViewDashboard={() => navigate({ to: "/dashboard" })}
        onBackToTemplates={() => navigate({ to: "/templates" })}
      />

      <PauseTemplateDialog
        open={isPauseDialogOpen}
        onOpenChange={setIsPauseDialogOpen}
        templateName={template?.name || ""}
        isPausing={updateConfigMutation.isPending}
        onPause={handlePause}
      />

      <RegenerateInstancesDialog
        open={isRegenerateDialogOpen}
        onOpenChange={setIsRegenerateDialogOpen}
        templateName={template?.name || ""}
        isRegenerating={updateConfigMutation.isPending}
        onRegenerate={handleRegenerateInstances}
      />
    </div>
  );
}
